import React, {useCallback} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import * as S from './styles/UGCCardChips.styles';

import UGCCard, {UGCCardProps} from './UGCChipsCard';
import {scrollToEl} from '@stencil/hotels-utils/src/scroll-into-view-details';

import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';

import {
  selRnrTagIds as selTagIdsSelectorFn,
  reviewDetailTypeSelector,
  rnrPaginatorStatus,
} from '@stencil/hotels-ugc-redux/src/hotels-ugc-selectors';
import {
  updatePaginatorStatus,
  userReviewDetailType,
  updateRnrTags,
} from '@stencil/hotels-ugc-redux/src/hotels-ugc-actions';

import {updateDetailsOpenCategorizedAmenities} from '@stencil/hotels-details-reducer/src/actions';
import {openCategorizedAmenitiesSelector} from '@stencil/hotels-details-reducer/src/selectors';

import detailsPageClickGaPush from '@stencil/hotels-utils/src/details-page-click-ga-push';
import {DetailsGAClickEventActions} from '../../../../../shared/config/ga-config';

import {DEFAULT_RNR_OFFSET, DEFAULT_RNR_LIMIT} from '@stencil/hotels-ugc-redux/src/ugc-config';

const AmenitiesAndServices: React.FC<UGCCardProps> = ({chips, onChipClick, type}) => (
  <UGCCard heading="Amenities & Services" chips={chips} onChipClick={onChipClick} type={type} />
);

const WhatGuestsLike: React.FC<UGCCardProps> = ({heading, chips, onChipClick, type}) => {
  let displayChips = chips.slice(0, 3);
  chips.length > 3 && displayChips.push({name: '+ more', id: ''});
  return (
    <UGCCard
      heading={heading}
      chips={displayChips}
      onChipClick={onChipClick}
      type={type}
      id="guests-like"
    />
  );
};

const WhatYouShouldKnow: React.FC<UGCCardProps> = ({heading, chips, onChipClick, type}) => {
  let displayChips = chips.slice(0, 3);
  chips.length > 3 && displayChips.push({name: '+more', id: ''});
  return (
    <S.UGCOuterWrapper>
      <UGCCard
        heading={heading}
        chips={displayChips}
        onChipClick={onChipClick}
        type={type}
        id="you-should-know"
      />
      <S.ReadAllReviewLink
        onClick={() => {
          detailsPageClickGaPush({
            eventAction: DetailsGAClickEventActions.readAllReviewsClick,
          });
          onChipClick();
        }}
      >
        Read all reviews
      </S.ReadAllReviewLink>
    </S.UGCOuterWrapper>
  );
};

interface IChipData {
  id: string;
  name: string;
}

interface IWhatYouShoudKnowChips {
  data: Array<IChipData>;
  title: string;
}

const UGCCardChips: React.FC<{
  whatGuestsLikeChips: Array<IWhatYouShoudKnowChips>;
  whatYouShouldKnowChips: Array<IWhatYouShoudKnowChips>;
  amenitiesData: string[];
}> = ({whatGuestsLikeChips, whatYouShouldKnowChips, amenitiesData}) => {
  const dispatch = useDispatch();
  const reviewDetailType = useSelector(reviewDetailTypeSelector);
  const paginatorStatus = useSelector(rnrPaginatorStatus, shallowEqual);
  const selTagIds = useSelector(selTagIdsSelectorFn, shallowEqual) || [];
  const {filter, sortBy} = reviewDetailType;

  const updatePaginationStatus = useCallback(
    status => {
      return dispatch(updatePaginatorStatus(status));
    },
    [paginatorStatus],
  );

  const updateSelTags = useCallback(
    data => {
      return dispatch(updateRnrTags(data));
    },
    [selTagIds],
  );

  const updateReviewDetail = useCallback(
    detailType => {
      return dispatch(userReviewDetailType(detailType));
    },
    [reviewDetailType],
  );

  const onChipClick = id => {
    if (!id) {
      scrollToEl(detailsNavigationIdsAndValues.guestReviews.id);
      return;
    }
    if (selTagIds.includes(id)) {
      updatePaginationStatus(paginatorStatus.filter(item => item !== `tag_${id}`));
      updateSelTags(selTagIds.filter(item => item !== id));
      updateReviewDetail({
        offset: DEFAULT_RNR_OFFSET,
        limit: DEFAULT_RNR_LIMIT,
        tagIds: selTagIds.filter(item => item !== id),
        filter: filter,
        sortBy: sortBy,
      });
    } else {
      updatePaginationStatus(paginatorStatus.concat(`tag_${id}`));
      updateSelTags(selTagIds.concat(id));
      updateReviewDetail({
        offset: DEFAULT_RNR_OFFSET,
        limit: DEFAULT_RNR_LIMIT,
        tagIds: selTagIds.concat(id),
        filter: filter,
        sortBy: sortBy,
      });
    }
    scrollToEl(detailsNavigationIdsAndValues.guestReviews.id);
    return;
  };

  const openCategorizedAmenities = useSelector(openCategorizedAmenitiesSelector);

  const onAmenityChipClick = () => {
    dispatch(
      updateDetailsOpenCategorizedAmenities({OpenCategorizedAmenities: !openCategorizedAmenities}),
    );
    scrollToEl(detailsNavigationIdsAndValues.amenities.id);
  };

  return (
    <S.UGCPersuasions>
      {whatGuestsLikeChips &&
      whatGuestsLikeChips.length > 0 &&
      whatYouShouldKnowChips &&
      whatYouShouldKnowChips.length > 0 ? (
        <S.UGCPersuasionsInnerWrap>
          <WhatGuestsLike
            heading={whatGuestsLikeChips[0].title}
            chips={whatGuestsLikeChips[0].data}
            onChipClick={onChipClick}
            type={'review'}
          />
          <WhatYouShouldKnow
            heading={whatYouShouldKnowChips[0].title}
            chips={whatYouShouldKnowChips[0].data}
            onChipClick={onChipClick}
            type={'review'}
          />
        </S.UGCPersuasionsInnerWrap>
      ) : (
        ''
      )}
      {(!whatGuestsLikeChips.length || !(whatYouShouldKnowChips.length > 0)) &&
      amenitiesData &&
      amenitiesData.length > 0 ? (
        <S.UGCPersuasionsInnerWrap>
          <AmenitiesAndServices
            heading="Amenities & Services"
            chips={amenitiesData}
            onChipClick={onAmenityChipClick}
            type={'amenity'}
          />
        </S.UGCPersuasionsInnerWrap>
      ) : (
        ''
      )}
    </S.UGCPersuasions>
  );
};

export default UGCCardChips;
